var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useMemo } from "react";
import { Accordion, AccordionSet, MessageBanner, MultiColumnList, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { kult0501DiagnosticGroupOf } from "../../utils/kult0501Diagnostics";
import css from "./Kult0501ReportResults.module.css";
var EMPHASIZED_ROW_CODES = ["0139", "0239", "0311", "0319", "0339"];
var NEWSPAPER_COLUMNS = [
    "label",
    "rowCode",
    "dailies",
    "otherNewspapersTotal",
    "twoToThreeWeekly",
    "weekly",
    "lessOften",
];
var MAGAZINE_COLUMNS = [
    "label",
    "rowCode",
    "total",
    "fourWeekly",
    "oneToThreeWeekly",
    "twoToThreeMonthly",
    "eightToTwelveYearly",
    "fiveToSevenYearly",
    "twoToFourYearly",
    "irregular",
];
var SUBJECT_COLUMNS = [
    "label",
    "rowCode",
    "totalTitles",
    "ceasedTitles",
    "newTitles",
];
var columnLabelId = function (column) {
    return "ui-br-legaldeposits.outputs.kult-05-01.column.".concat(column);
};
var buildFormatter = function (columns, formatNumber) {
    return Object.fromEntries(columns.map(function (column) { return [
        column,
        function (row) {
            var value = row[column];
            var content = typeof value === "number" ? formatNumber(value) : String(value !== null && value !== void 0 ? value : "");
            return EMPHASIZED_ROW_CODES.includes(row.rowCode) ? (React.createElement("span", { className: css.emphasizedRow }, content)) : (content);
        },
    ]; }));
};
var buildColumnMapping = function (columns, formatMessage) {
    return Object.fromEntries(columns.map(function (column) { return [
        column,
        formatMessage({ id: columnLabelId(column) }),
    ]; }));
};
var DiagnosticsBanner = function (_a) {
    var diagnostics = _a.diagnostics, type = _a.type, titleId = _a.titleId;
    return (React.createElement(MessageBanner, { type: type, className: css.diagnostics },
        React.createElement("strong", null,
            React.createElement(FormattedMessage, { id: titleId })),
        React.createElement("ul", { className: css.diagnosticList }, diagnostics.map(function (diagnostic) { return (React.createElement("li", { key: diagnostic.code },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.outputs.kult-05-01.diagnostic.item", values: { message: diagnostic.message } }))); }))));
};
export var Kult0501ReportResults = function (_a) {
    var data = _a.data;
    var _b = useIntl(), formatMessage = _b.formatMessage, formatNumber = _b.formatNumber;
    var unclassifiedDiagnostics = useMemo(function () {
        return data.diagnostics.filter(function (diagnostic) {
            return kult0501DiagnosticGroupOf(diagnostic.code) === "unclassified";
        });
    }, [data.diagnostics]);
    var incompleteValueDiagnostics = useMemo(function () {
        return data.diagnostics.filter(function (diagnostic) {
            return kult0501DiagnosticGroupOf(diagnostic.code) === "incompleteValue";
        });
    }, [data.diagnostics]);
    var methodologyDiagnostics = useMemo(function () {
        return data.diagnostics.filter(function (diagnostic) {
            return kult0501DiagnosticGroupOf(diagnostic.code) === "methodology";
        });
    }, [data.diagnostics]);
    var newspaperFormatter = useMemo(function () { return buildFormatter(NEWSPAPER_COLUMNS, formatNumber); }, [formatNumber]);
    var magazineFormatter = useMemo(function () { return buildFormatter(MAGAZINE_COLUMNS, formatNumber); }, [formatNumber]);
    var subjectFormatter = useMemo(function () { return buildFormatter(SUBJECT_COLUMNS, formatNumber); }, [formatNumber]);
    var newspaperColumnMapping = useMemo(function () { return buildColumnMapping(NEWSPAPER_COLUMNS, formatMessage); }, [formatMessage]);
    var magazineColumnMapping = useMemo(function () { return buildColumnMapping(MAGAZINE_COLUMNS, formatMessage); }, [formatMessage]);
    var subjectColumnMapping = useMemo(function () { return buildColumnMapping(SUBJECT_COLUMNS, formatMessage); }, [formatMessage]);
    return (React.createElement("section", null,
        React.createElement("h3", null,
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.outputs.kult-05-01.results.title", values: { libraryName: data.libraryName, year: data.year } })),
        unclassifiedDiagnostics.length > 0 && (React.createElement(DiagnosticsBanner, { diagnostics: unclassifiedDiagnostics, type: "warning", titleId: "ui-br-legaldeposits.outputs.kult-05-01.diagnostic.unclassified.title" })),
        incompleteValueDiagnostics.length > 0 && (React.createElement(DiagnosticsBanner, { diagnostics: incompleteValueDiagnostics, type: "default", titleId: "ui-br-legaldeposits.outputs.kult-05-01.diagnostic.incompleteValue.title" })),
        methodologyDiagnostics.length > 0 && (React.createElement(DiagnosticsBanner, { diagnostics: methodologyDiagnostics, type: "default", titleId: "ui-br-legaldeposits.outputs.kult-05-01.diagnostic.methodology.title" })),
        React.createElement(AccordionSet, null,
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.outputs.kult-05-01.section.partI",
                }), open: true },
                React.createElement(MultiColumnList, { contentData: data.newspaperRows, formatter: newspaperFormatter, visibleColumns: __spreadArray([], NEWSPAPER_COLUMNS, true), columnMapping: newspaperColumnMapping, interactive: false })),
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.outputs.kult-05-01.section.partII",
                }) },
                React.createElement(MultiColumnList, { contentData: data.magazineRows, formatter: magazineFormatter, visibleColumns: __spreadArray([], MAGAZINE_COLUMNS, true), columnMapping: magazineColumnMapping, interactive: false })),
            React.createElement(Accordion, { label: formatMessage({
                    id: "ui-br-legaldeposits.outputs.kult-05-01.section.partIII",
                }) },
                React.createElement(MultiColumnList, { contentData: data.subjectRows, formatter: subjectFormatter, visibleColumns: __spreadArray([], SUBJECT_COLUMNS, true), columnMapping: subjectColumnMapping, interactive: false })))));
};
